import { FC, useState, useEffect, useRef } from "react";

import Table from "@components/Table/index";
import CustomModal from "@components/customModel";
import { IModal, IVersion } from "@types";
import {
  ErrorServices,
  useFetchBenefitsPlanVersionsQuery,
  useUpdateBenefitsPlanVersionMutation,
} from "@services";
import { getVersionsColumns } from "../config";
import { useTypedSelector } from "@hooks";

interface IBenefitsPlanVersions extends IModal {
  download: (id?: number) => void;
}

const BenefitsPlanVersions: FC<IBenefitsPlanVersions> = ({
  isVisible,
  setIsVisible,
  download,
}) => {
  const { company } = useTypedSelector(state => state.auth.user!);
  const { id: company_id } = company!;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [versionId, setVersionId] = useState<number | null>(null);

  const [isDownloading, setIsDownloading] = useState(false);
  const downloadingVersionId = useRef<number | null>(null);

  const { data: versions, isLoading } =
    useFetchBenefitsPlanVersionsQuery({
      page,
      page_size: pageSize,
      company_id,
    });

  const [updateBenefitsPlanVersion] =
    useUpdateBenefitsPlanVersionMutation();

  const { data, pagination } = versions || {};

  useEffect(() => {
    if (data?.length) {
      const activeVersion = data.find(
        (version: IVersion) => version.is_active === "TRUE"
      );

      if (activeVersion?.id) {
        setVersionId(activeVersion.id);
      }
    }
  }, [data]);

  const handleActive = async (id: number) => {
    try {
      // Optional: optimistic UI update
      setVersionId(id);

      await updateBenefitsPlanVersion({
        company_id,
        version_id: id,
      }).unwrap();
    } catch (error) {
      ErrorServices(error);
    }
  };

  const handleDownload = async (id?: number) => {
  try {
    setIsDownloading(true);
    downloadingVersionId.current = id ?? null;

    await download(id);
  } catch (error: any) {
    ErrorServices(error);
  } finally {
    setIsDownloading(false);
    downloadingVersionId.current = null;
  }
};

  const versionsColumns = getVersionsColumns({
    handleActive,
    versionId,
    download: handleDownload,
    isDownloading,
    downloadingVersionId,
  });

  return (
    <CustomModal
      isVisible={isVisible}
      title="Previous Versions of Benefits Plans"
      onClose={() => setIsVisible(false)}
      footer={null}
      width="w-3/4"
      height="80vh"
    >
      <Table
        isLoading={isLoading}
        data={data}
        columns={versionsColumns}
        pagination
        customStyles="mx-4"
        count={pagination?.count}
        onChangePage={setPage}
        onPageSizeChange={setPageSize}
        page={page}
      />
    </CustomModal>
  );
};

export default BenefitsPlanVersions;